import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime } from 'rxjs';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { StockScrapService, StockScrapStats } from '../../../_services/stock-scrap.service';
import { AlertService } from '../../../_services/alert.service';
import { StockScrap } from '../../../_interfaces/stock-scrap';
import { CreateStockScrapComponent } from './create-stock-scrap/create-stock-scrap.component';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';

@Component({
    selector: 'app-stock-scrap',
    imports: [PaginatorPagesDirective, CommonModule, FormsModule, MainLayoutComponent, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule, DatePipe],
    templateUrl: './stock-scrap.component.html',
    styleUrl: './stock-scrap.component.css'
})
export class StockScrapComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly stockScrapService = inject(StockScrapService);
  private readonly alertService = inject(AlertService);

  scrapEntries: StockScrap[] = [];
  count = 0;
  stats: StockScrapStats | null = null;
  pageNumber = 0;
  itemsPerPage = 20;
  loading = true;
  loadError = false;

  keyword = '';
  warehouse = 'ALL';
  reason = 'ALL';
  source = 'ALL';

  readonly warehouseOptions = ['ALL', 'Kettering', 'Markstey'];
  readonly reasonOptions = [
    { value: 'ALL', label: 'Any reason' },
    { value: 'DAMAGED', label: 'Damaged' },
    { value: 'LOST', label: 'Lost' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'QUALITY_REJECT', label: 'Quality reject' },
    { value: 'OTHER', label: 'Other' },
  ];
  readonly sourceOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'auto', label: 'From delivery checks' },
    { value: 'manual', label: 'Logged by hand' },
  ];

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => {
      this.pageNumber = 0;
      this.load();
    });
  }

  ngOnInit(): void {
    this.load();
  }

  get filtersActive(): boolean {
    return !!(this.keyword || this.warehouse !== 'ALL' || this.reason !== 'ALL' || this.source !== 'ALL');
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.stockScrapService
      .listScrap({
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        keyword: this.keyword.trim() || undefined,
        warehouse: this.warehouse !== 'ALL' ? this.warehouse : undefined,
        reason: this.reason !== 'ALL' ? this.reason : undefined,
        source: this.source !== 'ALL' ? this.source : undefined,
      })
      .subscribe({
        next: (response) => {
          if (seq !== this.loadSeq) return;
          this.scrapEntries = response.items;
          this.count = response.count;
          if (response.stats) this.stats = response.stats;
          this.loading = false;
          this.loadError = false;
        },
        error: (error) => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error?.error?.message || 'Failed to load scrap log', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.keyword = '';
    this.pageNumber = 0;
    this.load();
  }

  setSource(value: string): void {
    this.source = this.source === value ? 'ALL' : value;
    this.pageNumber = 0;
    this.load();
  }

  applyFilters(): void {
    this.pageNumber = 0;
    this.load();
  }

  resetFilters(): void {
    this.keyword = '';
    this.warehouse = 'ALL';
    this.reason = 'ALL';
    this.source = 'ALL';
    this.pageNumber = 0;
    this.load();
  }

  updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 20;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  onCreateScrap(): void {
    this.dialog
      .open(CreateStockScrapComponent, { width: '640px', maxWidth: '96vw', maxHeight: '92vh' })
      .afterClosed()
      .subscribe((result) => {
        if (result?.status) this.load();
      });
  }

  // ---- What a row shows ----

  reasonStyle(reason: string): { label: string; cls: string; icon: string } {
    switch (reason) {
      case 'DAMAGED': return { label: 'Damaged', cls: 'bg-rose-50 text-rose-700', icon: 'broken_image' };
      case 'LOST': return { label: 'Lost', cls: 'bg-amber-100 text-amber-800', icon: 'help_outline' };
      case 'EXPIRED': return { label: 'Expired', cls: 'bg-orange-100 text-orange-800', icon: 'event_busy' };
      case 'QUALITY_REJECT': return { label: 'Quality reject', cls: 'bg-purple-50 text-purple-700', icon: 'thumb_down' };
      default: return { label: reason || 'Other', cls: 'bg-gray-100 text-gray-600', icon: 'tune' };
    }
  }

  loggedByName(email: string | null): string {
    return (email ?? '').split('@')[0] || 'Unknown';
  }

  cleanNotes(row: StockScrap): string {
    return (row.notes ?? '').replace(/^\[Delivery validation\]\s*/, '');
  }
}
